"use client";

import { useState, useEffect, useCallback } from "react";
import { handleDownloadBrochure } from "./utils";
import { Download } from "lucide-react";
import Link from "next/link";
import UserFormModal from "@/components/UserFormModal";

export default function CourseStickyBar({ course }) {
  const [isVisible, setIsVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [shouldDownload, setShouldDownload] = useState(false);

  // Scroll Visibility
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Download Handler
  const handleCourseDownload = useCallback(() => {
    setShouldDownload(true);
    setOpen(true);
  }, []);

  const handleFormSuccess = useCallback(() => {
    if (shouldDownload && course) {
      handleDownloadBrochure(course);
      setShouldDownload(false);
    }
  }, [shouldDownload, course]);

  return (
    <>
      <UserFormModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSuccess={handleFormSuccess}
        shouldDownload={shouldDownload}
      />

      {/* ==================== 1. MOBILE FLOATING ACTION BAR ==================== */}
      <div
        className={`
          fixed bottom-[80px] left-0 right-0 z-998 md:hidden px-4
          transition-all duration-300 ease-in-out
          ${
            isVisible
              ? "translate-y-0 opacity-100 pointer-events-auto"
              : "translate-y-6 opacity-0 pointer-events-none"
          }
        `}
      >
        <div className="flex items-center justify-center gap-2 max-w-sm mx-auto">
          {/* Download Brochure (Mobile Pill) */}
          {course?.BrochurePdf?.url && (
            <button
              onClick={handleCourseDownload}
              className="
                inline-flex items-center justify-center
                rounded-full bg-white border border-[#0077DF]
                px-4 py-2.5 text-[13px] font-bold text-[#0077DF]
                shadow-[0_4px_14px_rgba(0,0,0,0.12)] active:scale-95 transition-transform
                
              "
              aria-label="Download brochure"
              style={{ fontFamily: "saans-bold" }}
            >
              Download Brochure
            </button>
          )}

          {/* Apply Now (Mobile Pill) */}
          <Link
            href="https://application.msu.edu.in/campus-programme-registration"
            className="
              inline-flex items-center justify-center
              rounded-full bg-[#0077DF] border border-white
              px-4 py-2.5 text-[13px] font-bold text-white
              shadow-[0_4px_14px_rgba(0,119,223,0.3)] active:scale-95 transition-transform
            "
          >
            Apply Now
          </Link>
        </div>
      </div>

      {/* ==================== 2. DESKTOP STICKY BAR ==================== */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 hidden md:block
          bg-gray-50 border-t border-gray-300
          py-4 z-[1000]
          shadow-[0_-2px_8px_rgba(0,0,0,0.08)]
          transform transition-transform duration-300 ease-in-out
          page-container
          ${isVisible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        <div className="flex flex-row justify-between items-center gap-4">
          {/* Course Title */}
          <span
            className="text-[20px] text-black flex-1 min-w-0 pr-4"
            style={{ fontFamily: "saans-bold" }}
          >
            {course?.name || "Professional Course"}
          </span>

          {/* Buttons */}
          <div className="flex items-center gap-3 shrink-0">
            {course?.BrochurePdf?.url && (
              <button
                onClick={handleCourseDownload}
                className="
                  flex items-center gap-2
                  px-5 py-2.5
                  bg-white border border-gray-900
                  text-sm font-medium text-gray-900
                  hover:bg-gray-900 hover:text-white
                  transition-colors duration-300
                  cursor-pointer
                "
                aria-label="Download brochure"
              >
                <span>Download Brochure</span>
                <Download size={16} />
              </button>
            )}

            {/* Apply Now */}
            <Link
              href="https://application.msu.edu.in/campus-programme-registration"
              className="
                px-7 py-2.5
                bg-[#FFA201]
                text-sm font-semibold text-black
                hover:bg-[#FF8C00]
                transition-colors duration-300
                cursor-pointer
              "
            >
              Apply Now
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}